import React, { useState, useEffect, useRef } from "react";
import { signInWithEmailAndPassword, signOut } from "firebase/auth";
import { auth, db } from "../../../firebase";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { doc, getDoc, collection, query, where, getDocs, updateDoc, serverTimestamp, increment } from "firebase/firestore";
import { UserCreationService } from "../../../utils/userCreationService";
import { signInWithGooglePopup, signInWithGoogleRedirect, handleGoogleRedirectResult } from "../services/authService";
import { ensureUserDocForGoogle } from "../services/googleUserService";
import ResetPasswordModal from "./ResetPasswordModal";
import { LAUNCH } from "../../../constants/launchMessaging";
import { X } from "lucide-react";

const AGENT_LOGIN_EMAIL_STORAGE_KEY = "kawescar_agent_login_email";

function getDashboardPath(rol, tipoLogin, tipoUsuario) {
  if (rol === "admin") return "/admin";
  if (rol === "proveedor" || tipoLogin === "proveedor" || tipoUsuario === "proveedor") return "/dashboard/proveedor";
  if (rol === "agente") return "/agente";
  if (rol === "mecanico") return "/dashboard/mecanico";
  if (rol === "cliente") return "/dashboard/cliente";
  return "/dashboard/cliente";
}

export default function LoginModal({ initialEmail = "", tipoLogin: tipoLoginProp = null, onClose }) {
  const [email, setEmail]       = useState(initialEmail || "");
  const [password, setPassword] = useState("");
  const [error, setError]       = useState("");
  const [tipoUsuario, setTipoUsuario] = useState("");
  const [showPendienteInfo, setShowPendienteInfo] = useState(false);
  const navigate  = useNavigate();
  const location  = useLocation();
  const [user, loading] = useAuthState(auth);
  const [redirecting, setRedirecting]       = useState(false);
  const [redirectProgress, setRedirectProgress] = useState(0);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const hasRedirectedForUid = useRef(null);

  const tipoLogin = tipoLoginProp;

  // Prefill email from localStorage
  useEffect(() => {
    let emailFromAgent = "";
    try { emailFromAgent = localStorage.getItem(AGENT_LOGIN_EMAIL_STORAGE_KEY) || ""; } catch (_) {}
    const next = String(initialEmail || emailFromAgent).trim().toLowerCase();
    if (next && next.includes("@")) {
      setEmail(next);
      setTipoUsuario((c) => c || "cliente");
    }
  }, [initialEmail]);

  // Preselect tipo from localStorage
  useEffect(() => {
    if (tipoLogin || !email?.trim()) return;
    const emailTrim = email.trim().toLowerCase();
    if (!emailTrim.includes("@") || emailTrim.length < 5) return;
    try {
      const stored = localStorage.getItem("login_tipo_por_email");
      if (!stored) return;
      const map = JSON.parse(stored);
      const tipo = map[emailTrim];
      if (tipo === "proveedor" || tipo === "empresa") setTipoUsuario("proveedor");
      else if (tipo === "cliente") setTipoUsuario("cliente");
    } catch (_) {}
  }, [email, tipoLogin]);

  // Handle Google redirect result
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await handleGoogleRedirectResult();
      if (cancelled || !result.success || !result.user) return;
      try { await ensureUserDocForGoogle(result.user); } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, []);

  // Handle already-logged-in user
  useEffect(() => {
    if (!user?.uid) {
      hasRedirectedForUid.current = null;
      return;
    }
    if (hasRedirectedForUid.current === user.uid) return;
    let cancelled = false;
    setRedirecting(true);
    setRedirectProgress(0);
    const timeoutMs = 8000;
    const timeoutId = setTimeout(() => {
      if (cancelled) return;
      hasRedirectedForUid.current = user.uid;
      navigate("/dashboard/cliente", { replace: true });
      onClose?.();
    }, timeoutMs);
    const progressInterval = setInterval(() => {
      setRedirectProgress((p) => (p >= 85 ? 85 : p + 4));
    }, 120);
    (async () => {
      try {
        const userDoc = await getDoc(doc(db, "usuarios", user.uid));
        if (cancelled) return;
        clearTimeout(timeoutId);
        clearInterval(progressInterval);
        setRedirectProgress(100);
        if (userDoc.exists()) {
          const data = userDoc.data();
          try {
            const em = (user.email || "").trim().toLowerCase();
            if (em && (data.rol === "cliente" || data.rol === "proveedor" || data.rol === "empresa")) {
              const key = "login_tipo_por_email";
              const stored = localStorage.getItem(key);
              const map = stored ? JSON.parse(stored) : {};
              map[em] = data.rol === "cliente" ? "cliente" : "proveedor";
              localStorage.setItem(key, JSON.stringify(map));
            }
          } catch (_) {}
          if (data.rol === "pendiente_tipo" || data.accountType === "pendiente_tipo") {
            hasRedirectedForUid.current = user.uid;
            navigate("/select-account-type", { replace: true }); onClose?.(); return;
          }
          if (data.estado === "pendiente_registro" || data.estado === "inactivo" || data.activo === false) {
            setRedirecting(false); setRedirectProgress(0); return;
          }
          const s = data.status ?? data.estado;
          const isActivo = data.activo === true || s === "activo" || s === "activa";
          if (!isActivo && (s != null || data.activo === false)) {
            if (data.rol === "cliente" || data.accountType === "cliente") {
              hasRedirectedForUid.current = user.uid;
              // Mostrar panel de "solicitud en revisión" SIN cerrar el modal
              setRedirecting(false);
              setRedirectProgress(0);
              setShowPendienteInfo(true);
              await signOut(auth).catch(() => {});
              return;
            }
            if (data.rol === "empresa" || data.rol === "proveedor") {
              hasRedirectedForUid.current = user.uid;
              navigate("/empresa/pendiente", { replace: true }); onClose?.(); return;
            }
          }
          const path = getDashboardPath(data.rol, tipoLogin, tipoUsuario);
          hasRedirectedForUid.current = user.uid;
          navigate(path, { replace: true }); onClose?.(); return;
        }
        const isGoogle = user.providerData?.some((p) => p.providerId === "google.com");
        if (isGoogle) {
          try {
            const { created } = await ensureUserDocForGoogle(user);
            if (created) {
              hasRedirectedForUid.current = user.uid;
              navigate("/select-account-type", { replace: true }); onClose?.(); return;
            }
          } catch (_) {}
        }
        hasRedirectedForUid.current = user.uid;
        navigate("/dashboard/cliente", { replace: true }); onClose?.();
      } catch {
        if (!cancelled) {
          clearTimeout(timeoutId); clearInterval(progressInterval);
          setRedirecting(false); setRedirectProgress(0);
        }
      }
    })();
    return () => { cancelled = true; clearTimeout(timeoutId); clearInterval(progressInterval); };
  }, [user?.uid, navigate, tipoLogin, tipoUsuario, location.pathname]);

  const verificarAgenteNoRegistrado = async (email) => {
    try {
      const q = query(collection(db, "agentes"), where("email", "==", email), where("requiere_registro", "==", true));
      return !(await getDocs(q)).empty;
    } catch { return false; }
  };

  async function handleLogin(e) {
    e.preventDefault();
    setError("");
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      const userDoc = await getDoc(doc(db, "usuarios", cred.user.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        if (data.estado === "pendiente_registro") {
          await signOut(auth);
          if (data.rol === "agente") {
            setError(
              <div className="text-left space-y-2">
                <p className="font-semibold text-red-300">Registro pendiente</p>
                <p className="text-sm text-slate-400">Tu cuenta de agente aún no ha completado el registro inicial.</p>
                <button onClick={() => { navigate("/registro-agente"); onClose?.(); }} className="mt-1 rounded-xl bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 text-xs font-bold transition">
                  Completar Registro de Agente
                </button>
              </div>
            );
          } else {
            setError("Tu cuenta está pendiente de activación. Contacta al administrador.");
          }
          return;
        }
        if (data.estado === "inactivo" || data.activo === false) {
          await signOut(auth);
          setError("Tu cuenta está desactivada. Contacta al administrador.");
          return;
        }
        // Cliente con solicitud pendiente de aprobación → mostrar aviso en revisión
        const esClientePendiente = (data.status === "pendiente" || data.estado === "pendiente") &&
          (data.rol === "cliente" || data.accountType === "cliente");
        if (esClientePendiente) {
          await signOut(auth);
          // Marcar solicitud como "intentó acceder" para priorización en admin
          try {
            const snap = await getDocs(query(
              collection(db, "solicitudes_cliente"),
              where("email", "==", (cred.user.email || "").toLowerCase().trim()),
            ));
            if (!snap.empty) {
              await updateDoc(snap.docs[0].ref, {
                intentoAcceder: true,
                ultimoIntentoAcceso: serverTimestamp(),
                contadorIntentos: increment(1),
              });
            }
          } catch (_) {}
          setShowPendienteInfo(true);
          return;
        }
        try {
          const key = "login_tipo_por_email";
          const stored = localStorage.getItem(key);
          const map = stored ? JSON.parse(stored) : {};
          const em = (cred.user.email || email || "").trim().toLowerCase();
          if (em && (data.rol === "cliente" || data.rol === "proveedor" || data.rol === "empresa"))
            map[em] = data.rol === "cliente" ? "cliente" : "proveedor";
          localStorage.setItem(key, JSON.stringify(map));
        } catch (_) {}
        const path = getDashboardPath(data.rol, tipoLogin, tipoUsuario);
        navigate(path); onClose?.();
      } else {
        const esAgenteNoReg = await verificarAgenteNoRegistrado(email);
        if (esAgenteNoReg) {
          await signOut(auth);
          setError(
            <div className="text-left space-y-2">
              <p className="font-semibold text-red-300">Registro incompleto</p>
              <p className="text-sm text-slate-400">Tienes credenciales de agente pero no completaste el registro.</p>
              <button onClick={() => { navigate("/registro-agente"); onClose?.(); }} className="mt-1 rounded-xl bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 text-xs font-bold transition">
                Completar Registro de Agente
              </button>
            </div>
          );
        } else {
          await signOut(auth);
          setError("Usuario no encontrado en el sistema. Contacta al administrador.");
        }
      }
    } catch (err) {
      const badCred = err.code === "auth/invalid-credential" || err.code === "auth/user-not-found" || err.code === "auth/wrong-password";
      if (badCred) {
        setError(
          <div className="text-left space-y-2">
            <p className="font-semibold text-red-300">Credenciales incorrectas</p>
            <p className="text-sm text-slate-400">El email o la contraseña no son correctos.</p>
            <button onClick={() => setShowResetPassword(true)} className="mt-1 rounded-xl border border-blue-500/40 bg-blue-500/15 text-blue-300 hover:bg-blue-500/25 px-4 py-2 text-xs font-bold transition">
              Recuperar contraseña
            </button>
          </div>
        );
        return;
      }
      if (err.code === "auth/user-disabled") { setError("Tu cuenta está desactivada. Contacta al administrador."); return; }
      console.error("Error en login:", err.code, err.message);
      let usuarioExiste = false;
      let datosUsuario = null;
      let usuarioEnAuth = false;
      let tieneTemporal = false;
      let passwordTemporal = null;
      try {
        const snap = await getDocs(query(collection(db, "usuarios"), where("email", "==", email)));
        if (!snap.empty) {
          usuarioExiste = true;
          datosUsuario = snap.docs[0].data();
          try { const ac = await UserCreationService.checkUserExistsInAuth(email); usuarioEnAuth = ac.exists; } catch (_) {}
          if (datosUsuario.rol === "cliente") {
            try {
              const sq = await getDocs(query(collection(db, "solicitudes_cliente"), where("email", "==", email), where("usuario_creado", "==", true)));
              if (!sq.empty) {
                const sd = sq.docs[0].data();
                if (sd.password_temporal_generado && sd.password_temporal) { tieneTemporal = true; passwordTemporal = sd.password_temporal; }
              }
            } catch (_) {}
          }
        }
      } catch (_) {}
      if (usuarioExiste) {
        if (!usuarioEnAuth) {
          setError(
            <div className="text-left space-y-2">
              <p className="font-semibold text-red-300">Cuenta no activada en Firebase Auth</p>
              <p className="text-sm text-slate-400">Tu cuenta existe pero aún no está activada en autenticación. Contacta al administrador.</p>
              <button onClick={() => { navigate("/contacto"); onClose?.(); }} className="mt-1 rounded-xl border border-red-500/40 bg-red-500/15 text-red-300 hover:bg-red-500/25 px-4 py-2 text-xs font-bold transition">
                Contactar Administrador
              </button>
            </div>
          );
        } else if (tieneTemporal) {
          setError(
            <div className="text-left space-y-2">
              <p className="font-semibold text-blue-300">Contraseña Temporal Asignada</p>
              <p className="text-sm text-slate-400">Tu cuenta fue creada con una contraseña temporal:</p>
              <div className="rounded-xl border border-blue-500/30 bg-blue-500/10 p-3 text-center">
                <p className="text-base font-mono font-bold text-white">{passwordTemporal}</p>
              </div>
              <button
                onClick={async () => {
                  setPassword(passwordTemporal);
                  setError("");
                  setTimeout(async () => {
                    try { await signInWithEmailAndPassword(auth, email, passwordTemporal); }
                    catch { setError("Error al iniciar sesión. Intenta manualmente."); }
                  }, 300);
                }}
                className="w-full rounded-xl bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 text-xs font-bold transition"
              >
                Usar esta contraseña e iniciar sesión
              </button>
            </div>
          );
        } else {
          setError(
            <div className="text-left space-y-2">
              <p className="font-semibold text-yellow-300">Problema de autenticación</p>
              <p className="text-sm text-slate-400">Tu cuenta existe pero la contraseña es incorrecta o puedes tener una contraseña temporal.</p>
              <div className="flex gap-2">
                <button onClick={() => setShowResetPassword(true)} className="flex-1 rounded-xl border border-yellow-500/40 bg-yellow-500/10 text-yellow-300 hover:bg-yellow-500/20 px-3 py-2 text-xs font-bold transition">
                  Restablecer contraseña
                </button>
                <button onClick={() => { navigate("/contacto"); onClose?.(); }} className="flex-1 rounded-xl border border-white/15 bg-white/5 text-white/70 hover:bg-white/10 px-3 py-2 text-xs font-bold transition">
                  Contactar soporte
                </button>
              </div>
            </div>
          );
        }
      } else {
        if (err.code === "auth/invalid-email") setError("El formato del email no es válido.");
        else setError(`Error de conexión: ${err.message}. Intenta nuevamente o contacta al soporte.`);
      }
    }
  }

  async function handleGoogleLogin() {
    setError("");
    setGoogleLoading(true);
    try {
      const result = await signInWithGooglePopup();
      if (!result.success) {
        if (result.code === "auth/popup-blocked") {
          await signInWithGoogleRedirect();
          setRedirecting(true);
        } else if (result.code !== "auth/popup-closed-by-user") {
          setError(result.error || "Error al iniciar sesión con Google");
        }
      }
    } finally {
      setGoogleLoading(false);
    }
  }

  // ── Render ──

  const cardContent = (() => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400" />
        </div>
      );
    }

    if (showPendienteInfo) {
      return (
        <div className="px-6 py-8 text-center">
          <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-500/15 border border-amber-500/30 mx-auto">
            <svg className="h-8 w-8 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <h2 className="text-lg font-black text-white mb-2">Tu solicitud está en revisión</h2>
          <p className="text-sm text-slate-400 leading-relaxed mb-1">
            Recibimos tu solicitud y nuestro equipo la está evaluando.
          </p>
          <p className="text-sm text-slate-400 leading-relaxed mb-6">
            Te notificaremos por email cuando sea aprobada. Una vez aprobada, tendrás <span className="text-white font-semibold">30 días gratis</span> para explorar el dashboard.
          </p>
          <a
            href="https://wa.me/56912345678?text=Hola%2C%20quiero%20saber%20el%20estado%20de%20mi%20solicitud%20KawésCar"
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-center gap-2 w-full rounded-2xl border border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20 py-3 text-sm font-semibold text-emerald-300 transition mb-3"
          >
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            Consultar por WhatsApp
          </a>
          <button
            onClick={onClose}
            className="w-full rounded-2xl border border-white/10 py-2.5 text-sm font-medium text-slate-400 hover:text-white transition"
          >
            Cerrar
          </button>
        </div>
      );
    }

    if (user && redirecting) {
      return (
        <div className="py-10 text-center px-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-blue-500/20 text-blue-400 mb-5">
            <svg className="animate-spin h-7 w-7" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>
          <h2 className="text-base font-semibold text-white mb-1">{LAUNCH.redirigiendoCuenta}</h2>
          <p className="text-sm text-slate-400 mb-5">Preparando tu sesión...</p>
          <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full bg-blue-500 rounded-full transition-all duration-300" style={{ width: `${redirectProgress}%` }} />
          </div>
          <p className="text-xs text-slate-500 mt-2">{redirectProgress}%</p>
        </div>
      );
    }

    return (
      <div className="px-6 py-6">
        {/* Tarjeta cliente con beneficios del trial */}
        <div className="rounded-2xl border border-blue-500/30 bg-blue-500/10 px-4 py-3 mb-5">
          <div className="flex items-center gap-2.5 mb-2.5">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-600/80 text-xl">🚗</div>
            <div className="min-w-0">
              <p className="text-xs font-bold text-blue-300">{LAUNCH.loginTarjetaClienteTitulo}</p>
              <p className="text-[10px] text-blue-400/80 mt-0.5 leading-tight">{LAUNCH.loginTarjetaClienteTexto}</p>
            </div>
          </div>
          <div className="flex flex-col gap-1.5 border-t border-blue-500/20 pt-2.5">
            <div className="flex items-center gap-2">
              <span className="text-xs shrink-0">🎁</span>
              <p className="text-[11px] text-slate-300"><span className="font-bold text-white">30 días gratis</span> de Dashboard Básico al solicitar acceso</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs shrink-0">⚡</span>
              <p className="text-[11px] text-slate-300"><span className="font-bold text-amber-300">20% de descuento</span> si contratas un plan durante la prueba</p>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 rounded-2xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {error}
          </div>
        )}

        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide text-slate-400 mb-1.5">Correo electrónico</label>
            <input
              type="email" required placeholder="tu@email.com"
              value={email} onChange={e => setEmail(e.target.value)}
              className="w-full rounded-2xl border border-white/15 bg-white/10 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
            />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide text-slate-400 mb-1.5">Contraseña</label>
            <input
              type="password" required autoComplete="current-password" placeholder="••••••••"
              value={password} onChange={e => setPassword(e.target.value)}
              className="w-full rounded-2xl border border-white/15 bg-white/10 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
            />
            <button type="button" onClick={() => setShowResetPassword(true)} className="mt-1.5 text-xs text-blue-400 hover:text-blue-300 transition">
              ¿Olvidaste tu contraseña?
            </button>
          </div>
          <button type="submit" className="w-full rounded-2xl bg-blue-600 hover:bg-blue-500 text-white py-3 font-black text-sm transition">
            Iniciar sesión
          </button>
        </form>

        <div className="relative my-5">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
          <div className="relative flex justify-center"><span className="px-3 bg-slate-900 text-xs text-slate-500">o</span></div>
        </div>

        <button
          type="button" onClick={handleGoogleLogin} disabled={googleLoading}
          className="w-full flex items-center justify-center gap-2.5 rounded-2xl border border-white/15 bg-white/5 hover:bg-white/10 py-3 px-4 text-sm font-bold text-white transition disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {googleLoading ? (
            <svg className="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          ) : (
            <svg className="w-4 h-4" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
          )}
          Continuar con Google
        </button>

        <div className="mt-5 text-center space-y-2.5">
          <p className="text-xs text-slate-500">¿Aún no tienes acceso?</p>
          <button type="button" onClick={() => { navigate("/registro-cliente"); onClose?.(); }}
            className="w-full rounded-2xl border border-blue-500/30 bg-blue-500/10 hover:bg-blue-500/20 text-blue-300 py-2.5 text-xs font-bold transition flex items-center justify-center gap-1.5">
            🎁 {LAUNCH.ctaSolicitarCliente} — 30 días gratis
          </button>
        </div>
      </div>
    );
  })();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto">
      <div className="relative w-full max-w-md rounded-3xl overflow-hidden border border-white/10 my-4">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-white/10 px-6 py-4 bg-white/[0.03]">
          <div>
            <p className="text-[11px] font-black uppercase tracking-widest text-blue-400">KawésCar</p>
            <h2 className="text-lg font-black text-white">Iniciar sesión</h2>
          </div>
          <button
            type="button" onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-xl border border-white/20 text-white/60 hover:bg-white/10 hover:text-white transition"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950">
          {cardContent}
        </div>
      </div>

      <ResetPasswordModal isOpen={showResetPassword} onClose={() => setShowResetPassword(false)} initialEmail={email} />
    </div>
  );
}
